import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { driverApi, inr } from "../../lib/api";
import { toast } from "sonner";
import { RingModal } from "./RingModal";

// GLOBAL new ride / goods / FOOD alert for drivers. Mounted once in AppLayout so a request rings
// and pops on EVERY page — not just the dashboard. On the dashboard itself the in-page ring owns
// it, so this stays silent there. Accepting opens the dashboard where the full accept/claim flow runs.
export default function DriverRideRing() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onDashboard = pathname === "/app/driver";
  const active = user?.role === "driver" && !!user?.is_online && !onDashboard;

  const [reqs, setReqs] = useState([]);
  const [food, setFood] = useState([]);
  const [key, setKey] = useState(null);
  const snoozedUntil = useRef({});

  useEffect(() => {
    if (!active) { setReqs([]); setFood([]); setKey(null); return undefined; }
    let on = true;
    const poll = async () => {
      try {
        const [r, d] = await Promise.all([
          driverApi.rideRequests().catch(() => []),
          driverApi.deliveries().catch(() => ({ available: [] })),
        ]);
        if (!on) return;
        setReqs(r || []);
        setFood((d && d.available) || []);
      } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 10000);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { on = false; clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [active]);

  // Rides/goods take priority, then food deliveries. Snooze is keyed per job.
  const job = useMemo(() => {
    if (!active) return null;
    const now = Date.now();
    const fresh = (k) => !snoozedUntil.current[k] || snoozedUntil.current[k] <= now;
    const ride = (reqs || []).find((r) => fresh(`ride:${r.ride_id}`));
    if (ride) return { kind: "ride", key: `ride:${ride.ride_id}`, ...ride };
    const o = (food || []).find((x) => fresh(`food:${x.order_id}`));
    if (o) return { kind: "food", key: `food:${o.order_id}`, ...o };
    return null;
  }, [reqs, food, active, key]);

  useEffect(() => { setKey(job?.key || null); }, [job?.key]);

  const open = () => { setKey(null); navigate("/app/driver"); toast.info("Review and accept the request"); };
  const snooze = () => { if (job) snoozedUntil.current[job.key] = Date.now() + 60000; setKey(null); setReqs((p) => [...p]); toast.info("Snoozed — we'll ring again in 1 minute"); };

  if (!active || !job) return null;
  const isFood = job.kind === "food";
  const isGoods = job.kind === "ride" && job.purpose === "goods";
  const title = isFood ? "New delivery order!" : isGoods ? "New goods delivery!" : "New ride request!";
  const subtitle = isFood ? `${job.restaurant_name} · payout ${inr(job.delivery_fee)}` : `${job.pickup_address || "Pickup"} → ${job.drop_address || "Drop"}`;

  return (
    <RingModal
      open={!!job}
      accent="graphite"
      title={title}
      subtitle={subtitle}
      acceptLabel="Open to respond"
      rejectLabel="Later"
      onAccept={open}
      onReject={snooze}
      onSnooze={snooze}
    >
      <div className="space-y-1" data-testid="driver-global-ring">
        {isFood ? (
          <>
            <p className="text-base font-extrabold text-ink">{job.restaurant_name} · payout {inr(job.delivery_fee)}</p>
            <p className="text-sm text-ink-soft">Pick up: {job.restaurant_address || job.restaurant_name}</p>
            <p className="text-sm text-ink-soft">Deliver to: {job.delivery_address}</p>
          </>
        ) : (
          <>
            <p className="text-base font-extrabold text-ink">{inr(job.fare)} · {isGoods ? "Delivery" : (job.vehicle_type || "ride")}</p>
            <p className="text-sm text-ink-soft">{job.pickup_address}</p>
            <p className="text-sm text-ink-soft">→ {job.drop_address}</p>
          </>
        )}
      </div>
    </RingModal>
  );
}
